import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { PaperPlaneTilt, CheckCircle, Prohibit } from "@phosphor-icons/react";

const EMPTY = { sponsor_company: "", contact_name: "", contact_email: "", package_label: "Signature Partner", amount: "", appendix_notes: "" };
const STATUS_COLORS = { sent: "text-white/60", signed: "text-cyber-yellow", wire_pending: "text-cyber-yellow", paid: "text-cyber-green", revoked: "text-cyber-red" };

export default function AdminAgreements() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => api.get("/admin/agreements").then(({ data }) => setList(data)), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setMsg(""); setBusy(true);
    try {
      const { data } = await api.post("/admin/agreements", { ...form, amount: parseFloat(form.amount) });
      setMsg(`Agreement sent to ${data.contact_email}. Link: ${window.location.origin}/sponsor-agreement/${data.token}`);
      setForm(EMPTY); load();
    } catch (e) { setMsg(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Failed to create agreement"); }
    finally { setBusy(false); }
  };

  const markPaid = async (id) => {
    await api.post(`/admin/agreements/${id}/mark-paid`);
    load();
  };

  const revoke = async (id, company) => {
    if (!window.confirm(`Revoke the agreement link for ${company}? The link will stop working immediately.`)) return;
    await api.post(`/admin/agreements/${id}/revoke`);
    load();
  };

  return (
    <div data-testid="admin-agreements-page">
      <div className="term-label mb-2">/ sponsor contracts</div>
      <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl">Agreements.</h1>

      <div className="hard-border bg-cyber-surface p-6 mt-8 space-y-4 max-w-2xl" data-testid="agreement-create-form">
        <div className="term-label">/ new agreement</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <input placeholder="Sponsor company *" value={form.sponsor_company} onChange={(e) => setForm({ ...form, sponsor_company: e.target.value })} className="px-4 py-3" data-testid="agr-company" />
          <input placeholder="Contact name *" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="px-4 py-3" data-testid="agr-contact" />
          <input placeholder="Contact email *" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="px-4 py-3" data-testid="agr-email" />
          <input placeholder="Amount USD *" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="px-4 py-3" data-testid="agr-amount" />
        </div>
        <select value={form.package_label} onChange={(e) => setForm({ ...form, package_label: e.target.value })} className="w-full px-4 py-3 bg-cyber-black" data-testid="agr-package">
          {["Presenting Partner", "Signature Partner", "Supporting Partner", "Event Cohost", "Custom Package"].map((p) => <option key={p}>{p}</option>)}
        </select>
        <textarea placeholder="Package notes — agreed placements, guest allowance, exclusivity (optional)" value={form.appendix_notes} onChange={(e) => setForm({ ...form, appendix_notes: e.target.value })} rows={2} className="w-full px-4 py-3" data-testid="agr-notes" />
        {msg && <div className="font-mono-tech text-xs text-cyber-green break-all" data-testid="agr-msg">/ {msg}</div>}
        <button onClick={create} disabled={busy || !form.sponsor_company || !form.contact_email || !form.amount} className="btn-neon justify-center" data-testid="agr-create-btn">
          <PaperPlaneTilt size={16} weight="bold" /> {busy ? "Sending..." : "Create & email signing link"}
        </button>
      </div>

      <div className="hard-border mt-10 overflow-x-auto">
        <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-cyber-line font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/40">
          <div className="col-span-3">Sponsor</div><div className="col-span-2">Package</div><div className="col-span-2">Amount</div><div className="col-span-2">Status</div><div className="col-span-3">Actions</div>
        </div>
        {list.map((a) => (
          <div key={a.id} className="grid grid-cols-12 gap-2 px-5 py-4 border-b border-cyber-line items-center text-sm" data-testid={`agreement-row-${a.id}`}>
            <div className="col-span-3"><div className="font-bold">{a.sponsor_company}</div><div className="text-white/40 text-xs">{a.contact_email}</div></div>
            <div className="col-span-2 text-white/70">{a.package_label}</div>
            <div className="col-span-2 font-mono-tech">${Number(a.amount).toLocaleString()}</div>
            <div className={`col-span-2 font-mono-tech text-xs uppercase ${STATUS_COLORS[a.status] || "text-white/60"}`}>
              {a.status}{a.payment_method && ` · ${a.payment_method}`}
              {a.expires_at && !["paid", "revoked"].includes(a.status) && <div className="text-white/30 normal-case tracking-normal mt-0.5">expires {new Date(a.expires_at).toLocaleDateString()}</div>}
            </div>
            <div className="col-span-3 flex items-center gap-3 flex-wrap">
              <Link to={`/admin/agreements/${a.id}`} className="font-mono-tech text-xs text-cyber-yellow hover:text-white" data-testid={`view-contract-${a.id}`}>Contract</Link>
              <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}/sponsor-agreement/${a.token}`)} className="font-mono-tech text-xs text-white/60 hover:text-white" data-testid={`copy-link-${a.id}`}>Copy link</button>
              {a.status === "wire_pending" && (
                <button onClick={() => markPaid(a.id)} className="font-mono-tech text-xs text-cyber-green flex items-center gap-1" data-testid={`mark-paid-${a.id}`}><CheckCircle size={13} weight="bold" /> Wire received</button>
              )}
              {!["paid", "revoked"].includes(a.status) && (
                <button onClick={() => revoke(a.id, a.sponsor_company)} className="font-mono-tech text-xs text-cyber-red/80 hover:text-cyber-red flex items-center gap-1" data-testid={`revoke-${a.id}`}><Prohibit size={13} weight="bold" /> Revoke</button>
              )}
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="px-5 py-8 font-mono-tech text-white/40 text-sm">/ no agreements yet.</div>}
      </div>
    </div>
  );
}
